import React, { useState } from 'react';
import { IconButton, icons } from '@ublend-npm/aulaui-next';
import { Element } from 'slate';
import { ReactEditor, useSlateStatic } from 'slate-react';
import copy from 'copy-to-clipboard';
import { Iframe } from '../Iframe/Iframe';
import { EmbeddedIframe } from '../EmbeddedIframe/EmbeddedIframe';

import {
  Title,
  Link,
  Url,
  Description,
  Image,
  Content,
  Header,
  LoadingContainer,
  BlockLinkWrapper,
  BlockLinkContent,
  BlockLinkActions,
  CollapsibleEmbeddedIframeContainer,
  ContentWrapper,
  IconWrapper,
} from './BlockLink.styled';

import { useAulaEditorContext } from '../../EditorContext';
import { collapseLinkBlock } from '../../lib/commands';
import { Loading } from '../Loading/Loading';
import { BlockToolbar, BlockToolbarItem } from '../BlockToolbar/BlockToolbar';

import {
  isYoutubeUrl,
  getEmbedYoutubeUrl,
  isVimeoUrl,
  getEmbedVimeoUrl,
  isNearpodUrl,
  getEmbedNearpodUrl,
  isCollapsibleEmbeddedUrl,
  getCollapsibleEmbeddedProps,
} from './utils';

export type BlockLinkProps = {
  element: Element;
  isReadOnly: boolean;
  isSelected: boolean;
  onCopy: () => void;
  onDelete: () => void;
};

export const BlockLink = ({
  element,
  isReadOnly,
  isSelected,
  onCopy,
  onDelete,
}: BlockLinkProps) => {
  const { entities, updateEntity } = useAulaEditorContext();
  const editor = useSlateStatic();

  const [hovering, setHovering] = useState(false);

  const [copyTooltip, setCopyTooltip] = useState('Copy');

  if (element.entity == null) return null;

  const entity = entities[element.entity as string];

  if (entity == null || entity.type !== 'meta') return null;

  const { data, url } = entity;

  const embeddedUrlProps = getCollapsibleEmbeddedProps(url);

  const handleCopyLink = (e: React.SyntheticEvent) => {
    e.preventDefault();
    copy(url);
    setCopyTooltip('Copied');
    setTimeout(() => {
      setCopyTooltip('Copy link');
    }, 1000);
    onCopy();
  };

  if (data == null) {
    return (
      <LoadingContainer
        data-testid="block-link-loading"
        contentEditable={false}
      >
        <Loading />
      </LoadingContainer>
    );
  }

  const at = ReactEditor.findPath(editor, element);
  const toolbarItems: BlockToolbarItem[] = [
    {
      title: 'Delete',
      isVisible: !isReadOnly,
      icon: icons.Delete,
      onAction: onDelete,
    },
    {
      title: 'Collapse',
      icon: icons.FullscreenExit,
      isVisible: !isReadOnly,
      onAction: () => collapseLinkBlock(editor, at, url),
    },
    {
      title: copyTooltip,
      icon: icons.CopyLink,
      onAction: (e) => handleCopyLink(e),
    },
    {
      title: 'Open in new tab',
      label: 'Open',
      icon: icons.OpenInNew,
      onAction: () => window.open(url),
    },
  ];

  const blockToolbar = (
    <BlockToolbar isVisible={isSelected || hovering} items={toolbarItems} />
  );

  const defaultIframeProps = {
    title: data.title,
    isSelected,
    onMouseEnter: () => setHovering(true),
    onMouseLeave: () => setHovering(false),
    children: blockToolbar,
  };

  if (isYoutubeUrl(url)) {
    return (
      <Iframe
        {...defaultIframeProps}
        containerTestId="block-link-youtube"
        iframeTestId="block-link-youtube-iframe"
        allowFullScreen
        src={getEmbedYoutubeUrl(url)}
      />
    );
  }

  if (isVimeoUrl(url)) {
    return (
      <Iframe
        {...defaultIframeProps}
        containerTestId="block-link-vimeo"
        allowFullScreen
        src={getEmbedVimeoUrl(url)}
      />
    );
  }

  if (isNearpodUrl(url)) {
    return (
      <Iframe
        {...defaultIframeProps}
        containerTestId="block-link-nearpod"
        src={getEmbedNearpodUrl(url)}
      />
    );
  }

  return (
    <Link
      href={url}
      target="_blank"
      rel="noreferrer"
      tabIndex={-1}
      aria-label={data.title}
      data-testid="block-link"
      isSelected={isSelected}
      contentEditable={false}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <BlockLinkWrapper contentEditable={false}>
        <BlockLinkContent>
          {data.title !== url && (
            <Header>
              {data.icon && <Image alt={data.title} src={data.icon} />}
              <Title>{data.title}</Title>
            </Header>
          )}
          {blockToolbar}
          <ContentWrapper>
            {!data.icon && (
              <IconWrapper>
                <icons.Link />
              </IconWrapper>
            )}
            <Content hasIcon={!!data.icon}>
              {data.description && (
                <Description>{data.description}</Description>
              )}
              <Url>{url}</Url>
            </Content>
          </ContentWrapper>
        </BlockLinkContent>
        {isCollapsibleEmbeddedUrl(url) ? (
          <BlockLinkActions>
            <IconButton
              icon={entity.expanded ? icons.ArrowDropUp : icons.ArrowDropDown}
              type="secondary"
              size="small"
              onClick={(e: Event) => {
                e?.preventDefault();
                if (element.entity != null) {
                  updateEntity(element.entity, {
                    expanded: !entity.expanded,
                  });
                }
              }}
              label="collapsible-embedded-button"
            />
          </BlockLinkActions>
        ) : null}
      </BlockLinkWrapper>
      {entity.expanded ? (
        <CollapsibleEmbeddedIframeContainer>
          <EmbeddedIframe
            src={embeddedUrlProps.src}
            title={data.title}
            width={embeddedUrlProps.width}
            minWidth={embeddedUrlProps.minWidth}
            maxWidth={embeddedUrlProps.minWidth}
            minHeight={embeddedUrlProps.minHeight}
            maxHeight={embeddedUrlProps.maxHeight}
            oEmbedApi={embeddedUrlProps.oEmbedApi}
            oEmbedUrl={embeddedUrlProps.oEmbedUrl}
          />
        </CollapsibleEmbeddedIframeContainer>
      ) : null}
    </Link>
  );
};
